import type { CaptureResult, PostHog, Properties } from 'posthog-js'

import { defineNuxtPlugin, useRuntimeConfig } from '#app'
import posthog from 'posthog-js'

const REDACTED_PARAMS = ['invitation_code']

const REDACTION_STRING = '[REDACTED]'

// Recursively replace any string containing a redacted query param, covering top-level
// URL properties, nested ones like `$initial_person_info.u`, and arrays such as
// autocapture's `$elements` (each entry can carry an `attr__href`). Matching on the
// param name rather than a path prefix keeps redaction working regardless of where
// the param sits in the query string.
function redactValue(value: unknown): unknown {
  if (typeof value === 'string') {
    return REDACTED_PARAMS.some((param) => value.includes(`${param}=`)) ? REDACTION_STRING : value
  }
  if (Array.isArray(value)) {
    return value.map(redactValue)
  }
  if (value && typeof value === 'object') {
    redactObject(value as Properties)
  }
  return value
}

function redactObject(objectToRedact: Properties) {
  for (const [key, value] of Object.entries(objectToRedact)) {
    objectToRedact[key] = redactValue(value)
  }
}

function redactSensitiveUrls(event: CaptureResult | null): CaptureResult | null {
  if (!event) return null

  for (const properties of [event.properties, event.$set, event.$set_once]) {
    if (properties) redactObject(properties)
  }

  return event
}

export default defineNuxtPlugin(() => {
  const runtimeConfig = useRuntimeConfig()
  let posthogClient: PostHog | null = null

  if (runtimeConfig.public.posthogPublicKey) {
    posthogClient = posthog.init(runtimeConfig.public.posthogPublicKey, {
      api_host: runtimeConfig.public.posthogHost,
      ui_host: 'https://us.posthog.com',
      capture_pageview: 'history_change',
      before_send: redactSensitiveUrls,

      // Uncomment to enable debugging in development mode
      // loaded: (posthog) => {
      //   if (import.meta.env.MODE === 'development')
      //     posthog.debug()
      // },
    })
  }

  return {
    provide: {
      posthog: () => posthogClient,
    },
  }
})
